// ============================================
// State Configuration Exports
// All 50 states + DC
// ============================================
//
// ARCHITECTURE:
// 1. State-specific diagnostic files (detailed, regulation-specific)
//    - Located in ./diagnostics/{stateCode}.ts
//    - Use DiagnosticQuestionConfig format
//    - Preferred when available
//
// 2. Generated checklist (generic, auto-generated)
//    - Uses state regulatory data from all-states.ts
//    - Fallback when no diagnostic file exists
//
// ============================================

export * from './all-states';
export { FLORIDA_CHECKLIST } from './florida';

import type { DiagnosticQuestionConfig } from '../../types/diagnostic';
import { ALL_STATES, getStateByCode as getStateDataByCode } from './all-states';
import { getStateSpecificQuestions, hasStateSpecificQuestions, getStatesWithQuestionBanks } from '../diagnostic/states';

// ============================================
// TYPE DEFINITIONS
// ============================================

export type Severity = 'low' | 'medium' | 'high';
export type ComplianceStatus = 'COMPLIANT' | 'AT_RISK' | 'NON_COMPLIANT' | 'UNKNOWN';

export interface ChecklistItem {
  id: string;
  text: string;
  helpText?: string;
  regulatoryReference?: string;
  severity: Severity;
  category: string;
  order: number;
  // Extended fields from diagnostic format
  agencyName?: string;
  agencyPhone?: string;
  agencyUrl?: string;
}

export interface ChecklistCategory {
  id: string;
  name: string;
  description?: string;
  order: number;
  items: ChecklistItem[];
}

// ============================================
// CATEGORY ID CONSTANTS
// ============================================
// These IDs must match what Preview.tsx expects

export const CATEGORY_IDS = {
  // Section A: Licensing & Governance
  LICENSING: 'A',
  LICENSING_ALT: 'licensing-governance',

  // Section B: Background Checks
  BACKGROUND: 'B',
  BACKGROUND_ALT: 'background-screening',

  // Section C: Personnel
  PERSONNEL: 'C',
  PERSONNEL_ALT: 'personnel',

  // Section D: Home Health Aide
  HHA: 'D',
  HHA_ALT: 'home-health-aide',

  // Section E: Homemaker Services
  HOMEMAKER: 'E',
  HOMEMAKER_ALT: 'homemaker-services',

  // Section F: Care Delivery
  CARE_DELIVERY: 'F',
  CARE_DELIVERY_ALT: 'care-delivery',

  // Section G: Client Records
  RECORDS: 'G',
  RECORDS_ALT: 'client-records',

  // Section H: Survey & Compliance
  SURVEY: 'H',
  SURVEY_ALT: 'survey-compliance',

  // Section I: Training
  TRAINING: 'I',
  TRAINING_ALT: 'training',

  // Legacy IDs (for backward compatibility)
  LEGACY_LICENSING: 'licensing-operations',
  LEGACY_PERSONNEL: 'personnel-files',
  LEGACY_HEALTH: 'health-safety',
  LEGACY_TRAINING: 'training-competency',
  LEGACY_DOCUMENTATION: 'documentation-integrity',
} as const;

// ============================================
// NOTE: Diagnostic questions for all 51 states are available
// via getStateSpecificQuestions() from ../diagnostic/states
// ============================================

// ============================================
// NO-LICENSE STATES
// ============================================
// States that do not require a state license for home care agencies
// These states need special handling in the checklist

const NO_LICENSE_STATES: Set<string> = new Set([
  'AL', // Alabama - No state licensure for HHAs, Medicare certification only
  'MI', // Michigan - No state license for non-medical home care
  'ID', // Idaho - HHA license eliminated July 1, 2025
]);

/**
 * Check if a state requires home care agency licensure
 */
export function requiresStateLicense(stateCode: string): boolean {
  return !NO_LICENSE_STATES.has(stateCode.toUpperCase());
}

/**
 * Get list of states that don't require licensure
 */
export function getStatesWithNoLicenseRequired(): string[] {
  return Array.from(NO_LICENSE_STATES);
}


// ============================================
// TRANSFORM DIAGNOSTIC TO CHECKLIST
// ============================================

/**
 * Transform DiagnosticQuestionConfig[] to ChecklistCategory[]
 * Groups diagnostic questions by their itemId prefix (A, B, C, etc.)
 */
export function transformDiagnosticToChecklist(
  diagnostics: DiagnosticQuestionConfig[],
  stateCode: string
): ChecklistCategory[] {
  const state = getStateDataByCode(stateCode);
  if (!state) {
    throw new Error(`Unknown state code: ${stateCode}`);
  }

  // Group diagnostics by section (first letter of itemId)
  const sectionMap = new Map<string, DiagnosticQuestionConfig[]>();

  for (const diagnostic of diagnostics) {
    const section = diagnostic.itemId.charAt(0).toUpperCase();
    if (!sectionMap.has(section)) {
      sectionMap.set(section, []);
    }
    sectionMap.get(section)!.push(diagnostic);
  }

  // Section metadata
  const sectionMeta: Record<string, { name: string; description: string }> = {
    'A': {
      name: 'A. Licensing & Governance',
      description: `${state.regulatoryBodyAbbr} licensing and administrative requirements`
    },
    'B': {
      name: 'B. Background Checks',
      description: `${state.name} background screening requirements`
    },
    'C': {
      name: 'C. Personnel',
      description: `Employee documentation per ${state.regulatoryBodyAbbr} requirements`
    },
    'D': {
      name: 'D. Home Health Aide',
      description: 'Home health aide qualifications and supervision'
    },
    'E': {
      name: 'E. Homemaker Services',
      description: 'Homemaker training and supervision requirements'
    },
    'F': {
      name: 'F. Care Delivery',
      description: 'Care plans, EVV, and service documentation'
    },
    'G': {
      name: 'G. Client Records',
      description: 'Record keeping and retention requirements'
    },
    'H': {
      name: 'H. Survey & Compliance',
      description: 'Survey readiness and compliance documentation'
    },
    'I': {
      name: 'I. Training',
      description: `Staff training per ${state.regulatoryBodyAbbr} requirements`
    },
  };

  // Convert to categories
  const categories: ChecklistCategory[] = [];
  const sortedSections = Array.from(sectionMap.keys()).sort();

  for (let order = 0; order < sortedSections.length; order++) {
    const section = sortedSections[order];
    const diagnosticsInSection = sectionMap.get(section)!;
    const meta = sectionMeta[section] || {
      name: `Section ${section}`,
      description: ''
    };

    const items: ChecklistItem[] = diagnosticsInSection.map((diag, idx) => ({
      id: diag.id,
      text: diag.title,
      helpText: diag.description,
      regulatoryReference: diag.regulationCode
        ? `${diag.regulationCode} - ${diag.regulationName || ''}`
        : undefined,
      severity: diag.severity,
      category: section,
      order: idx + 1,
      agencyName: diag.agencyName,
      agencyPhone: diag.agencyPhone,
      agencyUrl: diag.agencyUrl,
    }));

    categories.push({
      id: section,
      name: meta.name,
      description: meta.description,
      order: order + 1,
      items,
    });
  }

  return categories;
}

// ============================================
// MAIN CHECKLIST GETTER
// ============================================

/**
 * Get checklist for a state
 * - First tries to use state-specific diagnostic questions (all 51 states)
 * - Falls back to generated generic checklist only if diagnostics unavailable
 */
export function getChecklistForState(stateCode: string): ChecklistCategory[] {
  const code = stateCode.toUpperCase();

  // Try to get state-specific diagnostic questions (available for all 51 states)
  const diagnostics = getStateSpecificQuestions(code);
  if (diagnostics && diagnostics.length > 0) {
    return transformDiagnosticToChecklist(diagnostics, code);
  }

  // Fall back to generated checklist (shouldn't happen since we have all 51 states)
  console.warn(`No diagnostic questions found for ${code}, using generated checklist`);
  return generateStateChecklist(code);
}

/**
 * Get checklist for a state (async version)
 * Same as sync version since diagnostics are pre-loaded
 */
export async function getChecklistForStateAsync(stateCode: string): Promise<ChecklistCategory[]> {
  return getChecklistForState(stateCode);
}

// ============================================
// SECTION A GENERATORS
// ============================================

/**
 * Generate Section A for states that DO NOT require state licensure
 * Focuses on Medicare certification, business operations, and best practices
 */
function generateNoLicenseSectionA(stateCode: string, state: any): ChecklistCategory {
  const noLicenseNotes: Record<string, string> = {
    'AL': 'Alabama does not require state licensure for home health agencies. Medicare certification is required for agencies providing skilled services.',
    'MI': 'Michigan does not require a state license for non-medical home care agencies. Home health agencies providing skilled nursing require Medicare certification.',
    'ID': 'Idaho eliminated the home health agency license requirement effective July 1, 2025. Medicare certification still required for skilled services.',
  };

  return {
    id: 'A',
    name: 'A. Business Operations & Certification',
    description: `${state.name} does not require state licensure. Focus on Medicare certification and business compliance.`,
    order: 1,
    items: [
      {
        id: `${stateCode}-A1`,
        text: `⚠️ ${state.name} does not require state home care agency licensure`,
        helpText: noLicenseNotes[stateCode] || `${state.name} does not have a state licensing requirement for home care agencies.`,
        severity: 'low',
        category: 'A',
        order: 1,
      },
      {
        id: `${stateCode}-A2`,
        text: 'Medicare certification obtained (if providing skilled services)',
        helpText: 'CMS certification required for Medicare/Medicaid billing. Survey conducted by state survey agency.',
        regulatoryReference: '42 CFR Part 484',
        severity: 'high',
        category: 'A',
        order: 2,
      },
      {
        id: `${stateCode}-A3`,
        text: 'Business license/registration current with state',
        helpText: 'Standard business registration required even without healthcare-specific license',
        severity: 'medium',
        category: 'A',
        order: 3,
      },
      {
        id: `${stateCode}-A4`,
        text: 'Medicaid provider enrollment completed (if serving Medicaid clients)',
        helpText: 'Enrollment required for Medicaid waiver program participation',
        severity: 'high',
        category: 'A',
        order: 4,
      },
      {
        id: `${stateCode}-A5`,
        text: 'Governing body/organizational structure established',
        helpText: 'Clear organizational authority and accountability required for Medicare certification',
        severity: 'medium',
        category: 'A',
        order: 5,
      },
      {
        id: `${stateCode}-A6`,
        text: 'Written policies and procedures documented',
        helpText: 'Required for Medicare certification and professional operations',
        severity: 'medium',
        category: 'A',
        order: 6,
      },
      {
        id: `${stateCode}-A7`,
        text: 'Quality assessment and performance improvement (QAPI) program in place',
        helpText: 'Required for Medicare-certified agencies per 42 CFR 484.65',
        regulatoryReference: '42 CFR 484.65',
        severity: 'high',
        category: 'A',
        order: 7,
      },
      {
        id: `${stateCode}-A8`,
        text: 'Patient rights policies established and documented',
        helpText: 'Federal requirement for Medicare-certified agencies',
        regulatoryReference: '42 CFR 484.50',
        severity: 'high',
        category: 'A',
        order: 8,
      },
      {
        id: `${stateCode}-A9`,
        text: 'Emergency preparedness plan documented',
        helpText: 'Required for Medicare certification per 42 CFR 484.102',
        regulatoryReference: '42 CFR 484.102',
        severity: 'high',
        category: 'A',
        order: 9,
      },
      {
        id: `${stateCode}-A10`,
        text: 'General liability insurance in place',
        helpText: 'Professional liability coverage recommended even if not state-mandated',
        severity: 'high',
        category: 'A',
        order: 10,
      },
      {
        id: `${stateCode}-A11`,
        text: 'Workers compensation insurance current',
        helpText: 'Required by state labor laws for employees',
        severity: 'high',
        category: 'A',
        order: 11,
      },
    ],
  };
}

/**
 * Generate Section A for states that DO require state licensure
 */
function generateLicensedSectionA(stateCode: string, state: any): ChecklistCategory {
  return {
    id: 'A',
    name: 'A. Licensing & Governance',
    description: `${state.regulatoryBodyAbbr} licensing, insurance, and operational policies`,
    order: 1,
    items: [
      {
        id: `${stateCode}-A1`,
        text: `${state.regulatoryBodyAbbr} ${state.licenseTypes[0]} license is current and active`,
        helpText: `License must be renewed per ${state.name} requirements`,
        regulatoryReference: state.regulatoryReferences.licensing || '',
        severity: 'high',
        category: 'A',
        order: 1,
      },
      {
        id: `${stateCode}-A2`,
        text: 'License is posted/available for inspection on premises',
        helpText: `Physical or digital copy must be readily accessible for ${state.regulatoryBodyAbbr} surveyors`,
        severity: 'medium',
        category: 'A',
        order: 2,
      },
      {
        id: `${stateCode}-A3`,
        text: `Ownership/administrator information current with ${state.regulatoryBodyAbbr}`,
        helpText: 'All changes must be reported within required timeframe',
        severity: 'medium',
        category: 'A',
        order: 3,
      },
      {
        id: `${stateCode}-A4`,
        text: 'Governing body with legal authority established',
        helpText: 'Required organizational structure per state regulations',
        severity: 'high',
        category: 'A',
        order: 4,
      },
      {
        id: `${stateCode}-A5`,
        text: 'Written policies and procedures are current and complete',
        helpText: `Must cover all required operational areas per ${state.name} requirements`,
        severity: 'medium',
        category: 'A',
        order: 5,
      },
      {
        id: `${stateCode}-A6`,
        text: 'Quality management/assurance program documented',
        helpText: 'Ongoing program to evaluate and ensure quality of care',
        severity: 'medium',
        category: 'A',
        order: 6,
      },
      {
        id: `${stateCode}-A7`,
        text: 'Grievance procedure established and documented',
        helpText: 'Process for clients to voice concerns and complaints',
        severity: 'medium',
        category: 'A',
        order: 7,
      },
      {
        id: `${stateCode}-A8`,
        text: 'Client rights information provided and documented',
        helpText: 'Clients must be informed of their rights',
        severity: 'high',
        category: 'A',
        order: 8,
      },
      {
        id: `${stateCode}-A9`,
        text: 'Emergency preparedness plan is documented',
        helpText: 'Must include procedures for natural disasters and emergencies',
        severity: 'medium',
        category: 'A',
        order: 9,
      },
      ...(state.requirements.bondRequired ? [{
        id: `${stateCode}-A10`,
        text: 'Surety/fidelity bond in place and current',
        helpText: 'Required for licensure - verify bond is active',
        severity: 'high' as const,
        category: 'A',
        order: 10,
      }] : []),
      {
        id: `${stateCode}-A11`,
        text: `General liability insurance (${state.requirements.insuranceMinimum} minimum) in place`,
        helpText: 'Must maintain current certificate of insurance',
        severity: 'high',
        category: 'A',
        order: 11,
      },
    ],
  };
}

// ============================================
// GENERATED CHECKLIST (FALLBACK)
// ============================================

/**
 * Generate a state-specific checklist based on state regulatory data
 * Used as fallback when no diagnostic file exists
 */
export function generateStateChecklist(stateCode: string): ChecklistCategory[] {
  const state = getStateDataByCode(stateCode);
  if (!state) {
    throw new Error(`Unknown state code: ${stateCode}`);
  }

  // Check if this is a no-license state
  const isNoLicenseState = NO_LICENSE_STATES.has(stateCode.toUpperCase());

  // Generate Section A based on license requirements
  const sectionA: ChecklistCategory = isNoLicenseState
    ? generateNoLicenseSectionA(stateCode, state)
    : generateLicensedSectionA(stateCode, state);

  return [
    sectionA,

    // B. Background Checks
    {
      id: 'B',
      name: 'B. Background Checks',
      description: `${state.name} background screening requirements`,
      order: 2,
      items: [
        {
          id: `${stateCode}-B1`,
          text: `${state.backgroundCheck.type === 'both' ? 'State and FBI' : 'State'} background check completed before employment`,
          helpText: `${state.backgroundCheck.registryChecks.slice(0, 2).join(' and ')} background check required`,
          regulatoryReference: state.regulatoryReferences.backgroundScreening || '',
          severity: 'high',
          category: 'B',
          order: 1,
        },
        {
          id: `${stateCode}-B2`,
          text: 'Background check results documented in file',
          helpText: 'Eligibility letter or verification on file',
          severity: 'high',
          category: 'B',
          order: 2,
        },
        {
          id: `${stateCode}-B3`,
          text: 'OIG/GSA exclusion list checks completed',
          helpText: 'Verify employee is not excluded from federal programs',
          severity: 'high',
          category: 'B',
          order: 3,
        },
        {
          id: `${stateCode}-B4`,
          text: `Re-screening completed per ${state.name} requirements (${state.backgroundCheck.frequency})`,
          helpText: `${state.name} requires rescreening ${state.backgroundCheck.frequency.toLowerCase()}`,
          severity: 'high',
          category: 'B',
          order: 4,
        },
        ...state.backgroundCheck.registryChecks
          .filter(r => !['FBI', 'OIG'].includes(r) && r !== state.backgroundCheck.registryChecks[0])
          .slice(0, 3)
          .map((registry, index) => ({
            id: `${stateCode}-B${5 + index}`,
            text: `${registry} check completed`,
            helpText: `${state.name} requires ${registry} verification`,
            severity: 'high' as const,
            category: 'B',
            order: 5 + index,
          })),
      ],
    },

    // C. Personnel
    {
      id: 'C',
      name: 'C. Personnel',
      description: `Employee documentation per ${state.regulatoryBodyAbbr} requirements`,
      order: 3,
      items: [
        {
          id: `${stateCode}-C1`,
          text: 'Personnel file maintained for each employee',
          helpText: 'Centralized file with all required documents',
          severity: 'high',
          category: 'C',
          order: 1,
        },
        {
          id: `${stateCode}-C2`,
          text: 'Government-issued photo ID on file',
          helpText: `Valid ${state.name} driver's license, passport, or state ID`,
          severity: 'medium',
          category: 'C',
          order: 2,
        },
        {
          id: `${stateCode}-C3`,
          text: 'Professional licenses verified (RN, LPN, CNA, etc.)',
          helpText: 'Must verify with appropriate licensing board',
          severity: 'high',
          category: 'C',
          order: 3,
        },
        {
          id: `${stateCode}-C4`,
          text: 'Signed job description on file',
          helpText: 'Must outline duties, responsibilities, and qualifications',
          severity: 'medium',
          category: 'C',
          order: 4,
        },
        {
          id: `${stateCode}-C5`,
          text: 'Employee health requirements documented',
          helpText: 'Free of communicable diseases that could affect clients',
          severity: 'high',
          category: 'C',
          order: 5,
        },
        {
          id: `${stateCode}-C6`,
          text: 'I-9 Employment Eligibility Verification completed',
          helpText: 'Federal requirement for all employees',
          regulatoryReference: '8 CFR 274a.2',
          severity: 'high',
          category: 'C',
          order: 6,
        },
        {
          id: `${stateCode}-C7`,
          text: 'Performance evaluations documented',
          helpText: 'Regular evaluation of employee performance',
          severity: 'medium',
          category: 'C',
          order: 7,
        },
      ],
    },

    // D. Home Health Aide
    {
      id: 'D',
      name: 'D. Home Health Aide',
      description: 'HHA qualifications and supervision requirements',
      order: 4,
      items: [
        {
          id: `${stateCode}-D1`,
          text: 'HHA certification/training requirements met',
          helpText: `${state.name} HHA qualification requirements`,
          severity: 'high',
          category: 'D',
          order: 1,
        },
        {
          id: `${stateCode}-D2`,
          text: 'Competency evaluation completed and documented',
          helpText: 'Skills assessment for direct care tasks',
          severity: 'high',
          category: 'D',
          order: 2,
        },
        ...(state.requirements.rnSupervision ? [{
          id: `${stateCode}-D3`,
          text: 'RN supervisory visits documented per requirements',
          helpText: 'RN supervision required for skilled services',
          severity: 'high' as const,
          category: 'D',
          order: 3,
        }] : []),
        {
          id: `${stateCode}-D4`,
          text: 'Care plan instructions provided to HHA',
          helpText: 'Written instructions for care delivery',
          severity: 'medium',
          category: 'D',
          order: 4,
        },
        ...(state.requirements.tbScreening ? [{
          id: `${stateCode}-D5`,
          text: 'TB screening current for all direct care staff',
          helpText: 'Two-step TB test or chest X-ray on hire, annual assessment thereafter',
          severity: 'high' as const,
          category: 'D',
          order: 5,
        }] : []),
      ],
    },

    // E. Homemaker Services (if applicable)
    {
      id: 'E',
      name: 'E. Homemaker Services',
      description: 'Homemaker training and supervision (if applicable)',
      order: 5,
      items: [
        {
          id: `${stateCode}-E1`,
          text: 'Homemaker training requirements met',
          helpText: 'Training on homemaker tasks and client interaction',
          severity: 'medium',
          category: 'E',
          order: 1,
        },
        {
          id: `${stateCode}-E2`,
          text: 'Homemaker supervision documented',
          helpText: 'Regular supervisory visits for homemaker services',
          severity: 'medium',
          category: 'E',
          order: 2,
        },
        {
          id: `${stateCode}-E3`,
          text: 'Scope of services clearly defined',
          helpText: 'Homemakers provide only non-medical services',
          severity: 'medium',
          category: 'E',
          order: 3,
        },
      ],
    },

    // F. Care Delivery
    {
      id: 'F',
      name: 'F. Care Delivery',
      description: 'Care plan management and service documentation',
      order: 6,
      items: [
        {
          id: `${stateCode}-F1`,
          text: 'Individualized care plan for each client',
          helpText: 'Care plan completed before services begin',
          severity: 'high',
          category: 'F',
          order: 1,
        },
        {
          id: `${stateCode}-F2`,
          text: 'Care plan reviewed and updated as required',
          helpText: 'Regular review and updates based on client condition',
          severity: 'medium',
          category: 'F',
          order: 2,
        },
        ...(state.requirements.evvRequired ? [{
          id: `${stateCode}-F3`,
          text: 'Electronic Visit Verification (EVV) system in place',
          helpText: `${state.name} requires EVV for Medicaid-funded services`,
          regulatoryReference: '21st Century Cures Act',
          severity: 'high' as const,
          category: 'F',
          order: 3,
        }] : []),
        {
          id: `${stateCode}-F4`,
          text: 'Service documentation complete and timely',
          helpText: 'All visits documented with required information',
          severity: 'high',
          category: 'F',
          order: 4,
        },
        {
          id: `${stateCode}-F5`,
          text: 'Missed visit policy and follow-up documented',
          helpText: 'Protocol for handling missed or late visits',
          severity: 'medium',
          category: 'F',
          order: 5,
        },
      ],
    },

    // G. Client Records
    {
      id: 'G',
      name: 'G. Client Records',
      description: 'Record keeping and retention requirements',
      order: 7,
      items: [
        {
          id: `${stateCode}-G1`,
          text: 'Client record maintained for each client',
          helpText: 'Comprehensive record with all required documentation',
          severity: 'high',
          category: 'G',
          order: 1,
        },
        {
          id: `${stateCode}-G2`,
          text: 'Records safeguarded against loss or unauthorized access',
          helpText: 'Physical and electronic security measures in place',
          severity: 'high',
          category: 'G',
          order: 2,
        },
        {
          id: `${stateCode}-G3`,
          text: `Record retention meets ${state.name} requirements`,
          helpText: 'Records retained for minimum required period',
          severity: 'medium',
          category: 'G',
          order: 3,
        },
        {
          id: `${stateCode}-G4`,
          text: 'HIPAA privacy and security policies in place',
          helpText: 'Compliance with federal privacy requirements',
          regulatoryReference: '45 CFR Parts 160, 164',
          severity: 'high',
          category: 'G',
          order: 4,
        },
      ],
    },

    // H. Survey & Compliance
    {
      id: 'H',
      name: 'H. Survey & Compliance',
      description: 'Survey readiness and regulatory compliance',
      order: 8,
      items: [
        {
          id: `${stateCode}-H1`,
          text: 'Survey deficiency response process documented',
          helpText: 'Plan of correction procedures in place',
          severity: 'high',
          category: 'H',
          order: 1,
        },
        {
          id: `${stateCode}-H2`,
          text: 'Incident reporting process documented and followed',
          helpText: `Critical incidents reported to ${state.regulatoryBodyAbbr} within required timeframe`,
          severity: 'high',
          category: 'H',
          order: 2,
        },
        {
          id: `${stateCode}-H3`,
          text: 'Audit-ready documentation maintained',
          helpText: `Files organized for ${state.regulatoryBodyAbbr} surveyor review`,
          severity: 'medium',
          category: 'H',
          order: 3,
        },
        {
          id: `${stateCode}-H4`,
          text: 'Medicare/Medicaid certification maintained (if applicable)',
          helpText: 'Federal certification requirements met',
          severity: 'high',
          category: 'H',
          order: 4,
        },
      ],
    },

    // I. Training
    {
      id: 'I',
      name: 'I. Training',
      description: `Staff training per ${state.regulatoryBodyAbbr} requirements`,
      order: 9,
      items: [
        {
          id: `${stateCode}-I1`,
          text: `Initial training completed (${state.training.initialHours} hours required)`,
          helpText: 'Comprehensive orientation before providing care',
          regulatoryReference: state.regulatoryReferences.training || '',
          severity: 'high',
          category: 'I',
          order: 1,
        },
        {
          id: `${stateCode}-I2`,
          text: `Ongoing training hours tracked (${state.training.annualHours} hours/year)`,
          helpText: 'Annual in-service training requirements',
          severity: 'high',
          category: 'I',
          order: 2,
        },
        {
          id: `${stateCode}-I3`,
          text: 'Abuse/neglect recognition and reporting training',
          helpText: `Mandatory reporter training per ${state.name} law`,
          severity: 'high',
          category: 'I',
          order: 3,
        },
        {
          id: `${stateCode}-I4`,
          text: 'Infection control training completed',
          helpText: 'Initial and ongoing training on infection prevention',
          severity: 'high',
          category: 'I',
          order: 4,
        },
        {
          id: `${stateCode}-I5`,
          text: 'CPR/First Aid certification current',
          helpText: 'Must be current and from approved provider',
          severity: 'high',
          category: 'I',
          order: 5,
        },
        ...state.training.specialRequirements
          .filter(r => !['CPR', 'First Aid'].includes(r))
          .slice(0, 3)
          .map((req, index) => ({
            id: `${stateCode}-I${6 + index}`,
            text: `${req} training completed`,
            helpText: `${state.name}-specific requirement`,
            severity: 'medium' as const,
            category: 'I',
            order: 6 + index,
          })),
      ],
    },
  ];
}

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get all available state options for dropdown
 */
export function getStateOptions(): { value: string; label: string }[] {
  return ALL_STATES.map(state => ({
    value: state.code,
    label: state.name,
  }));
}

/**
 * Check if a state code is valid
 */
export function isValidStateCode(code: string): boolean {
  return ALL_STATES.some(s => s.code === code.toUpperCase());
}

/**
 * Check if a state has diagnostic questions available
 */
export function hasDiagnosticFile(stateCode: string): boolean {
  return hasStateSpecificQuestions(stateCode);
}

/**
 * Get list of states with diagnostic questions
 */
export function getStatesWithDiagnostics(): string[] {
  return getStatesWithQuestionBanks();
}

/**
 * Get state data by code (re-export for convenience)
 */
export { getStateDataByCode as getStateByCode };

/**
 * Get total item count for a checklist
 */
export function getChecklistItemCount(checklist: ChecklistCategory[]): number {
  return checklist.reduce((sum, cat) => sum + cat.items.length, 0);
}

/**
 * Get item counts by severity
 */
export function getChecklistSeverityCounts(checklist: ChecklistCategory[]): {
  high: number;
  medium: number;
  low: number;
} {
  const counts = { high: 0, medium: 0, low: 0 };

  for (const category of checklist) {
    for (const item of category.items) {
      counts[item.severity]++;
    }
  }

  return counts;
}
